// src/pages/Register.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import provinces from '../data/provinces.json';
import cities from '../data/cities.json';
import useAuthStore from '../store/authStore';
import axios from 'axios';

const Register = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone_number: '',
    password: '',
    confirm_password: '',
    province_id: '',
    city: '',
    address: '',
    postal_code: '',
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [filteredCities, setFilteredCities] = useState([]);

  // مرحله OTP
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(true);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'province_id') {
      const selectedProvinceId = Number(value);
      const provinceCities = cities.filter(c => c.province_id === selectedProvinceId);
      setFilteredCities(provinceCities);
      setFormData({ ...formData, province_id: value, city: '' });
    } else {
      setFormData({ ...formData, [name]: value });
    }

    // پاک کردن خطای فیلد
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.first_name.trim()) newErrors.first_name = 'نام الزامی است';
    if (!formData.last_name.trim()) newErrors.last_name = 'نام خانوادگی الزامی است';
    if (!formData.phone_number.match(/^09\d{9}$/)) newErrors.phone_number = 'شماره موبایل معتبر نیست';
    if (formData.password.length < 6) newErrors.password = 'رمز عبور حداقل ۶ کاراکتر';
    if (formData.password !== formData.confirm_password) newErrors.confirm_password = 'رمزها مطابقت ندارند';
    if (!formData.province_id) newErrors.province_id = 'استان را انتخاب کنید';
    if (!formData.city) newErrors.city = 'شهر را انتخاب کنید';
    if (!formData.address.trim()) newErrors.address = 'آدرس الزامی است';
    if (!formData.postal_code.match(/^\d{10}$/)) newErrors.postal_code = 'کد پستی باید ۱۰ رقم باشد';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // مرحله اول: ارسال OTP
  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors(prev => ({ ...prev, general: '' }));

    try {
      await axios.post('/auth/send-otp', {
        phone_number: formData.phone_number
      });

      setShowOtpModal(true);
      setResendTimer(120);
      setCanResend(false);
      alert('کد تأیید با موفقیت به شماره شما ارسال شد');
    } catch (error) {
      console.error('خطا در ارسال OTP:', error);
      const message = error.response?.data?.message || 'خطا در ارسال کد تأیید';
      setErrors(prev => ({ ...prev, general: message }));
    } finally {
      setIsLoading(false);
    }
  };

  // تایمر ارسال مجدد
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else if (resendTimer === 0) {
      setCanResend(true);
    }
  }, [resendTimer]);

  // مرحله دوم: تأیید OTP و ثبت‌نام نهایی
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setOtpError('');

    if (otp.length < 6) {
      setOtpError('کد باید ۶ رقمی باشد');
      return;
    }

    setOtpLoading(true);

    try {
      const response = await axios.post('/auth/register', {
        phone_number: formData.phone_number,
        otp,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        password: formData.password,
        province: provinces.find(p => p.id === Number(formData.province_id))?.name || '',
        city: formData.city,
        address: formData.address.trim(),
        postal_code: formData.postal_code,
      });

      const { accessToken, user } = response.data;

      // ذخیره کاربر و توکن در Zustand
      useAuthStore.getState().login(user, accessToken);

      alert('عضویت با موفقیت انجام شد! خوش آمدید 👋');
      setShowOtpModal(false);
      navigate('/');
    } catch (error) {
      console.error('خطا در ثبت‌نام نهایی:', error);
      const message = error.response?.data?.message || 'خطایی در تأیید کد رخ داد';
      setOtpError(message);
    } finally {
      setOtpLoading(false);
    }
  };

  // ارسال مجدد OTP
  const handleResendOtp = async () => {
    setOtp('');
    setOtpError('');
    setCanResend(false);

    try {
      await axios.post('/auth/send-otp', {
        phone_number: formData.phone_number
      });
      setResendTimer(120);
      alert('کد جدید با موفقیت ارسال شد');
    } catch (error) {
      console.error('خطا در ارسال مجدد:', error);
      setOtpError(error.response?.data?.message || 'خطا در ارسال مجدد کد');
      setCanResend(true); // اجازه تلاش مجدد
    }
  };

  return (
    <>
      {/* فرم اصلی ثبت‌نام */}
      <div className="min-h-screen bg-gradient-to-br from-gold-light via-white to-gold-light py-12 px-4 flex items-center justify-center">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-gold to-gold-dark text-white py-8 px-6 text-center">
              <h1 className="text-2xl md:text-3xl font-bold">عضویت در خرمی‌گلد</h1>
              <p className="mt-2 text-gold-light text-base">اطلاعات خود را وارد کنید</p>
            </div>

            <div className="p-6 md:p-10">
              {errors.general && <p className="text-red-500 text-center mb-4 font-medium">{errors.general}</p>}

              <form onSubmit={handleRegisterSubmit} className="space-y-5">
                {/* نام و نام خانوادگی */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-gray-700 font-medium mb-1 text-sm">نام</label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/50 text-base ${errors.first_name ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="علی"
                    />
                    {errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>}
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-1 text-sm">نام خانوادگی</label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/50 text-base ${errors.last_name ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="احمدی"
                    />
                    {errors.last_name && <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>}
                  </div>
                </div>

                {/* شماره موبایل */}
                <div>
                  <label className="block text-gray-700 font-medium mb-1 text-sm">شماره موبایل</label>
                  <input
                    type="tel"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleChange}
                    maxLength="11"
                    dir="ltr"
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/50 text-base ${errors.phone_number ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="۰۹۱۲۳۴۵۶۷۸۹"
                  />
                  {errors.phone_number && <p className="text-red-500 text-xs mt-1">{errors.phone_number}</p>}
                </div>

                {/* استان و شهر */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-gray-700 font-medium mb-1 text-sm">استان</label>
                    <select
                      name="province_id"
                      value={formData.province_id}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/50 text-base ${errors.province_id ? 'border-red-500' : 'border-gray-300'}`}
                    >
                      <option value="">انتخاب استان</option>
                      {provinces.map(prov => (
                        <option key={prov.id} value={prov.id}>{prov.name}</option>
                      ))}
                    </select>
                    {errors.province_id && <p className="text-red-500 text-xs mt-1">{errors.province_id}</p>}
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-1 text-sm">شهر</label>
                    <select
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      disabled={!formData.province_id}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/50 text-base ${errors.city ? 'border-red-500' : 'border-gray-300'}`}
                    >
                      <option value="">ابتدا استان را انتخاب کنید</option>
                      {filteredCities.map(city => (
                        <option key={city.id} value={city.name}>{city.name}</option>
                      ))}
                    </select>
                    {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                  </div>
                </div>

                {/* آدرس دقیق */}
                <div>
                  <label className="block text-gray-700 font-medium mb-1 text-sm">آدرس دقیق</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    rows="3"
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/50 text-base ${errors.address ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="خیابان، کوچه، پلاک، واحد"
                  />
                  {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
                </div>

                {/* کد پستی */}
                <div>
                  <label className="block text-gray-700 font-medium mb-1 text-sm">کد پستی</label>
                  <input
                    type="text"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleChange}
                    maxLength="10"
                    dir="ltr"
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/50 text-base ${errors.postal_code ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="۱۲۳۴۵۶۷۸۹۰"
                  />
                  {errors.postal_code && <p className="text-red-500 text-xs mt-1">{errors.postal_code}</p>}
                </div>

                {/* رمز عبور */}
                <div>
                  <label className="block text-gray-700 font-medium mb-1 text-sm">رمز عبور</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/50 text-base ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="حداقل ۶ کاراکتر"
                  />
                  {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-1 text-sm">تکرار رمز عبور</label>
                  <input
                    type="password"
                    name="confirm_password"
                    value={formData.confirm_password}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/50 text-base ${errors.confirm_password ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {errors.confirm_password && <p className="text-red-500 text-xs mt-1">{errors.confirm_password}</p>}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-gold hover:bg-gold-dark disabled:opacity-70 text-white font-bold py-4 rounded-lg transition transform hover:scale-105 shadow-md text-lg mt-6"
                >
                  {isLoading ? 'در حال ارسال کد...' : 'عضویت'}
                </button>
              </form>

              <p className="text-center mt-6 text-gray-600 text-sm">
                قبلاً عضو شده‌اید؟ <Link to="/login" className="text-gold font-bold hover:underline">ورود</Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* مودال تأیید OTP */}
      {showOtpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-8">
            <h2 className="text-2xl font-bold text-center text-gray-800 mb-4">تأیید شماره موبایل</h2>
            <p className="text-center text-gray-600 mb-8">
              کد ۶ رقمی ارسال‌شده به <span className="font-bold" dir="ltr">{formData.phone_number}</span> را وارد کنید
            </p>

            <form onSubmit={handleOtpSubmit}>
              <input
                type="text"
                value={otp}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setOtp(value);
                  setOtpError('');
                }}
                maxLength="6"
                autoComplete="one-time-code"
                className={`w-full px-6 py-5 border-2 text-center text-3xl tracking-widest rounded-xl focus:outline-none focus:ring-4 focus:ring-gold/50 transition ${otpError ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="------"
                dir="ltr"
                autoFocus
              />
              {otpError && <p className="text-red-500 text-center text-sm mt-3">{otpError}</p>}

              <p className="text-center text-sm text-gray-500 mt-6">
                {canResend ? (
                  <button type="button" onClick={handleResendOtp} className="text-gold hover:underline font-medium">
                    ارسال مجدد کد
                  </button>
                ) : (
                  <>ارسال مجدد در {resendTimer} ثانیه</>
                )}
              </p>

              <button
                type="submit"
                disabled={otpLoading || otp.length < 6}
                className="w-full mt-8 bg-gold hover:bg-gold-dark disabled:opacity-70 text-white font-bold py-4 rounded-xl transition shadow-lg text-lg"
              >
                {otpLoading ? 'در حال تأیید...' : 'تأیید و عضویت'}
              </button>
            </form>

            <button
              onClick={() => setShowOtpModal(false)}
              className="w-full mt-4 text-gray-500 hover:text-gray-700 text-sm"
            >
              بازگشت
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Register;